import { cloneDeep, parseInt } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DashboardHeader,
  useAppDispatch,
  useAppSelector,
  useEffectOnce,
  CustomForm, CustomButtonGroup, CustomBreadCrumbs, DashboardLoader, CustomRadioForm, FilterProp, CheckBox,
  SidePopover
} from '../../../globalUtils/globalExports';
import { getUserReducer} from '../../../rmsReduxStore/reduxExports';
import { useNavigate } from 'react-router-dom';
import { ICustomFormProps, IFormContentProp } from '../../../globalUtils/CustomForm/CustomForm';
import { useLocation } from 'react-router-dom';
import { Id, toast } from 'react-toastify';
import '../../../components/users/UserForm/UserForm.css';
import { AxiosResponse } from 'axios';
import { checkValidAlphaNumericAndSymbols, createBreadCrumbProps, keyPressValidationAlphaNumericAndSymbols } from '../../../globalUtils/globalHooks';
import { NotificationActionStatusSidePopover } from '../../../components/componentExports';
import { createAcAlarmRule, fetchAcSingleRule, setAcLatestConfigurationOnTop, updateAcAlarmRule } from '../../rmsReduxStore/acConfigureNotificationRedux/acConfigureNotificationCreators';
import AddIcon from '@mui/icons-material/Add';
import './AcCreateRule.css';
import { IconWithTooltip } from '../../../globalUtils/globalIcons';
import { Button } from '@mui/material';
import { fetchAcChargerLocationReferenceData, fetchAcChargerProductTypeReferenceData, fetchAcChargerStationReferenceData, fetchAcCustomerReferenceData, fetchAcRuleAlarmTypeReferenceData, fetchAcSoftwarVersionReferenceData } from '../../rmsReduxStore/acReferenceDataRedux/acReferenceDataCreator';
import { getAcReferenceDataReducer } from '../../rmsReduxStore/acReduxExports';
import AcCreateRuleAlertLogic from './AcCreateRuleAlertLogic';
import { fetchAcChargersByCustomersByOrg } from '../../../rmsReduxStore/usersRedux/userCreators';

export interface AcSmsRecipientInterface {
  countryCode: string;
  phoneNumber: string;
}

const AcCreateRule: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { acChargerListByCustomerByOrg } = useAppSelector(getUserReducer);
  const {
    acChargerLocationReferenceData,
    chargerProductTypeReferenceData,
    chargingStationReferenceData,
    softwareVersionReferenceData,
    acCustomerReferenceData,
  } = useAppSelector(getAcReferenceDataReducer);

  const initialFormData: AcCreateRuleFormData = {
    name: '',
    description: '',
    chargerIds: [],
    logicComplexity: 'Basic',
    alarmTypeId: '',
    alarmContinuesFor: '',
    alarmType: { severity: '', id: '', name: '' },
    autoAssign: 'Enabled',
  };

  const [createRuleFormData, setCreateRuleFormData] = useState(initialFormData);
  const [dataList, setDataList] = useState<string[]>([]);
  const [mode, setMode] = useState(location?.state?.mode ? location?.state?.mode : 'Create');
  const [showLoader, setShowLoader] = useState(false);
  const [alarmList, setAlarmList] = useState<IAcAlarmType[]>([]);
  const [showSms, setShowSms] = useState<boolean>(false);
  const [showEmail, setShowEmail] = useState<boolean>(false);
  const handleSetShowSms = (value) => setShowSms(value);
  const handleSetShowEmail = (value) => setShowEmail(value);
  const [smsRecipients, setSmsRecipients] = useState<AcSmsRecipientInterface[]>([]);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [smsRecipientsInitially, setSmsRecipientsInitially] = useState<AcSmsRecipientInterface[]>();
  const [emailRecipientsInitially, setEmailRecipientsInitially] = useState<string[]>();
  const [selectedChargersInitially, setSelectedChargersInitially] = useState<string[]>();
  const [alarmTypeIdInitially, setAlarmTypeIdInitially] = useState<string[]>();
  const [filters, setFilters] = useState<InnerFilterState[]>([
    { key: 'customer', value: null },
    { key: 'chargingStationId', value: null },
    { key: 'productType', value: null },
    { key: 'location', value: null },
    { key: 'softwareVersion', value: null },
  ]);
  const [chargersChipValues, setChargersChipValues] = useState<string[]>([]);
  const [chargerCheckboxStates, setChargerCheckboxStates] = useState<string[]>([]);
  const [selectedRadioOption, setSelectedRadioOption] = useState('SelectChargers');
  const [selectedChargers, setSelectedChargers] = useState<string[]>();
  const [isSelectAllChargersChecked, setIsSelectAllChargersChecked] = useState(false);
  const orgId = JSON.parse(localStorage.getItem('rms-user') || '{}').orgId;
  const [errorMessage, setErrorMessage] = useState('');
  const [errorMessageDescription, setErrorMessageDescription] = useState('');
  const [isNotificationActionSidePopoverOpen, setIsNotificationActionSidePopoverOpen] = useState<boolean>(false);

  const handleClose = () => setIsNotificationActionSidePopoverOpen(false);
  const handleSmsRecipientsChange = (value) => setSmsRecipients(value);
  const handleEmailRecipientsChange = (value) => setEmailRecipients(value);

  useEffect(() => {
    const chargerData = acChargerListByCustomerByOrg?.map((item) => ({
      header: item?.customerName,
      count: item?.chargers?.length,
      data: item?.chargers?.map((charger) => charger?.visibleId),
    })) || [];
    const dataList = chargerData?.flatMap(({ header, count, data }) => [
      (header !== undefined && count !== undefined) ? `${header} (${count})` : '',
      ...data,
    ]).filter(item => item !== '');
    setDataList(Array.from(new Set(dataList)));
  }, [acChargerListByCustomerByOrg]);

  const handleFetchAlarmTypes = async () => {
    try {
      const res = await dispatch(fetchAcRuleAlarmTypeReferenceData());
      const response = JSON.parse(res);
      if (response?.length) {
        setAlarmList(response);
        return response;
      }
    } catch (error) {
      console.error('Error fetching AC alarm types:', error);
    }
  };

  useEffectOnce(async () => {
    dispatch(fetchAcChargerLocationReferenceData());
    dispatch(fetchAcSoftwarVersionReferenceData());
    dispatch(fetchAcCustomerReferenceData());
    dispatch(fetchAcChargerProductTypeReferenceData());
    dispatch(fetchAcChargerStationReferenceData());
    chargerListCallback?.(orgId);
    setShowLoader(true);
    setMode(location?.state?.mode || 'Create');
    const alarmResponse = await handleFetchAlarmTypes();

    if (mode && (location?.state?.userId || location?.state?.ruleId)) {
      dispatch(fetchAcSingleRule({}, location?.state?.userId)).then((res) => {
        setSelectedChargersInitially(res?.data?.data?.chargerIds);
        setAlarmTypeIdInitially(res?.data?.data?.notificationTriggerIds);
        setSelectedChargers(res?.data?.data?.chargerIds);
        setChargerCheckboxStates(res?.data?.data?.chargerIds);
        handleSmsRecipientsChange(res?.data?.data?.smsRecipients);
        handleEmailRecipientsChange(res?.data?.data?.emailRecipients);
        setEmailRecipientsInitially(res?.data?.data?.emailRecipients);
        setSmsRecipientsInitially(res?.data?.data?.smsRecipients);
        setChargersChipValues(res?.data?.data?.chargerIds);
        setIsSelectAllChargersChecked(
          res?.data?.data?.chargerIds?.length === dataList?.filter((item) => !item.endsWith(')'))?.length,
        );
        setCreateRuleFormData((prevState) => ({
          ...prevState,
          alarmTypeId: res?.data?.data?.notificationTriggerIds,
          alarmType: alarmResponse?.filter(alarm => res?.data?.data?.notificationTriggerIds?.includes(alarm.id)) ?? null,
          autoAssign: res?.data?.data?.autoAssign,
        }));
      });
    }
    setShowLoader(false);
  });

  useEffect(() => {
    if (location?.state?.rowData) {
      setCreateRuleFormData({
        ...createRuleFormData,
        name: location?.state?.rowData?.name || location?.state?.rowData?.ruleName,
        description: location?.state?.rowData?.description,
        alarmContinuesFor: location?.state?.rowData?.alarmContinuesFor,
      });
      if (location.state?.rowData?.notifyUsingEmail === 'Yes') setShowEmail(true);
      if (location.state?.rowData?.notifyUsingSms === 'Yes') setShowSms(true);
    }
  }, []);

  const handleFormDataChange = useCallback((val, formKey) => {
    const formData = cloneDeep(createRuleFormData);
    if (val?.id) {
      formData[formKey] = val?.id;
    } else {
      if (formKey === 'alarmTypeId') {
        formData[formKey] = val.map(item => item.id);
      } else {
        formData[formKey] = val;
      }
    }
    setCreateRuleFormData(formData);

    if (formKey === 'name') {
      setErrorMessage(val.length > 90 ? 'Max characters allowed are 90' : '');
      formData[formKey] = formData[formKey].charAt(0).toUpperCase() + formData[formKey].slice(1);
    }
    if (formKey === 'description') {
      setErrorMessageDescription(val.length > 500 ? 'Max characters allowed are 500' : '');
      formData[formKey] = formData[formKey].charAt(0).toUpperCase() + formData[formKey].slice(1);
    }
    if (formKey === 'alarmContinuesFor') {
      if (val && val <= 0) {
        toast.error('\'Alarm Continues For\' field expects only positive integers.');
        formData[formKey] = null;
        return;
      }
      if (val > 240) {
        toast.error('Max value can be 240 minutes.');
        formData[formKey] = null;
        return;
      }
    }
  }, [createRuleFormData]);

  function arraysAreEqual<T>(arr1?: T[], arr2?: T[]): boolean {
    if (!arr2 || !arr1) return false;
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  }

  function arraysEqualIgnoreOrder(arr1?: string[], arr2?: string[]): boolean {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return sorted1.every((val, i) => val === sorted2[i]);
  }

  const disableSubmitButton = useCallback(() => {
    if (showSms === false && showEmail === false) return true;
    if (mode === 'Create') {
      return !(
        createRuleFormData?.name?.length > 0 &&
        createRuleFormData.name.length <= 150 &&
        (showSms || showEmail) &&
        selectedChargers && selectedChargers?.length > 0 &&
        createRuleFormData.alarmTypeId?.length &&
        createRuleFormData?.description?.length <= 500
      );
    } else if (mode === 'Edit') {
      if (createRuleFormData?.description?.length > 500) return true;
      if (selectedChargers?.length === 0) return true;
      if (createRuleFormData?.alarmTypeId?.length === 0) return true;

      const currentAlarmTypeIds = Array.isArray(createRuleFormData?.alarmTypeId)
        ? createRuleFormData?.alarmTypeId as string[]
        : createRuleFormData?.alarmTypeId ? [createRuleFormData?.alarmTypeId as string] : [];

      return (
        createRuleFormData?.name === location.state?.rowData?.name &&
        createRuleFormData?.description === location.state?.rowData?.description &&
        arraysEqualIgnoreOrder(selectedChargers, selectedChargersInitially) &&
        createRuleFormData.alarmContinuesFor === location.state?.rowData?.alarmContinuesFor &&
        showEmail === (location.state?.rowData?.notifyUsingEmail === 'No' ? false : true) &&
        showSms === (location.state?.rowData?.notifyUsingSms === 'No' ? false : true) &&
        arraysAreEqual(smsRecipientsInitially, smsRecipients) &&
        arraysAreEqual(emailRecipientsInitially, emailRecipients) &&
        arraysEqualIgnoreOrder(currentAlarmTypeIds, alarmTypeIdInitially) &&
        selectedChargers && selectedChargers?.length > 0
      );
    }
  }, [createRuleFormData, showSms, showEmail, selectedChargers, smsRecipients, emailRecipients, alarmTypeIdInitially]);

  const userFormSuccessToast = (): Id => toast.success(mode === 'Edit' ? 'Notification Rule Edited.' : 'Notification Rule Added.');
  const userFormErrorToast = (message): Id => toast.error(message);

  const footerButtonList = useMemo(() => [
    {
      buttonText: mode === 'Edit' ? 'Update' : 'Create',
      buttonId: 'submit',
      btnClassName: disableSubmitButton() ? 'primary__btn disabled' : 'primary__btn',
      handleClick: (): void => { handleSubmit?.(mode, showSms, showEmail); },
      isDisabled: disableSubmitButton(),
      buttonVariant: 'filled',
    },
    {
      buttonText: 'Cancel',
      buttonId: 'cancel',
      btnClassName: 'secondary__btn',
      handleClick: (): void => { navigate('/ac/configure-notification'); },
      isDisabled: false,
      buttonVariant: 'outlined',
    },
  ], [disableSubmitButton, createRuleFormData, mode, showSms, showEmail, selectedChargers]);

  const createRuleFormArray: IFormContentProp[] = [
    {
      rowCountClassName: '',
      sectionHeading: '',
      formObject: [
        {
          formLabel: 'Notification Name',
          isRequired: true,
          objectType: 'input',
          selectDropdownProps: null,
          inputProps: {
            inputPlaceHolder: 'Type here',
            handleInputChange: (event): void => {
              handleFormDataChange(checkValidAlphaNumericAndSymbols(event?.target?.value), 'name');
            },
            inputValue: createRuleFormData?.name,
            inputType: 'string',
            inputFieldId: 'ac_rule_name',
            isRequired: false,
            isDisabled: location?.state?.mode === 'Edit',
            autoFocus: true,
            errorMessage: errorMessage,
            handleKeyPress: keyPressValidationAlphaNumericAndSymbols,
          },
          textAreaProps: null,
          isFixedWidth: true,
        },
        {
          formLabel: 'Description',
          isRequired: false,
          objectType: 'text-area',
          selectDropdownProps: null,
          inputProps: null,
          isFixedWidth: true,
          textAreaProps: {
            inputPlaceHolder: mode !== 'View' ? 'Type here' : '',
            handleInputChange: (event): void => {
              handleFormDataChange(event?.target?.value, 'description');
            },
            isRequired: false,
            inputValue: createRuleFormData?.description,
            inputFieldId: 'ac_rule_description',
            isDisabled: false,
            className: 'custom-resize',
            errorMessage: errorMessageDescription,
          },
        },
      ],
    },
  ];

  const createRuleFormProps: ICustomFormProps = useMemo(() => ({
    formContainerClassName: 'create__rule__name__description',
    formArray: createRuleFormArray,
  }), [createRuleFormData, mode, acCustomerReferenceData]);

  const handleSubmit = useCallback(async (mode, sms, email) => {
    const reqBody = {
      name: createRuleFormData?.name,
      description: createRuleFormData?.description,
      chargerIds: selectedChargers,
      logicComplexity: createRuleFormData?.alarmContinuesFor ? 'Advanced' : 'Basic',
      alarmContinuesFor: parseInt(createRuleFormData?.alarmContinuesFor),
      notifyUsingSms: sms ? 'Yes' : 'No',
      notifyUsingEmail: email ? 'Yes' : 'No',
      id: location?.state?.userId,
      autoAssign: createRuleFormData?.autoAssign,
      smsRecipients: showSms ? smsRecipients : [],
      emailRecipients: showEmail ? emailRecipients : [],
      notificationTriggerIds: createRuleFormData?.alarmTypeId,
      notificationTriggerType: 'Alarm',
    };

    let response: AxiosResponse;
    setShowLoader(true);
    if (mode === 'Edit') {
      response = await dispatch(updateAcAlarmRule(reqBody));
    } else {
      response = await dispatch(createAcAlarmRule(reqBody));
    }

    if ((response?.status === 200 || response?.status === 202) && response?.data?.message === 'Success') {
      setShowLoader(false);
      userFormSuccessToast();
      await dispatch(setAcLatestConfigurationOnTop(true));
      navigate('/ac/configure-notification');
    } else {
      setShowLoader(false);
      userFormErrorToast(response?.data?.message);
    }
  }, [createRuleFormData, mode, selectedChargers, smsRecipients, emailRecipients, showSms, showEmail]);

  const breadCrumbs = createBreadCrumbProps({
    breadCrumbProps: [
      { objectType: 'link', id: 'acCreateNotificationRule' },
      {
        objectType: 'text',
        id: 'text',
        text: mode !== 'Create' ? createRuleFormData?.name : `${mode} Notification Rule`,
      },
    ],
  });

  const setHeaderText = useCallback(() => {
    if (mode === 'Create') return `${mode} Notification Rule`;
    return createRuleFormData?.name;
  }, [mode, createRuleFormData]);

  const chargerListCallback = useCallback((orgId, filters?, searchTerm?) => {
    let filtersToBeSent: Filters[] = [];
    if (filters && filters?.length > 0) {
      filtersToBeSent = filters?.map((item) => ({
        key: item.key,
        values: item.value?.map((item) => item.id) || [],
      }));
    }
    dispatch(fetchAcChargersByCustomersByOrg({
      orgId: orgId,
      filters: filters ? filtersToBeSent : [],
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : '',
    }));
  }, [createRuleFormData]);

  const handleFilterDataChange = useCallback((val, filterkey) => {
    const filtersToUpdate = cloneDeep(filters);
    const selectedFilter = filtersToUpdate?.find((filter) => filter?.key === filterkey);
    if (selectedFilter) {
      selectedFilter.value = Array.isArray(val) ? val : [val];
    }
    setFilters(filtersToUpdate);
    chargerListCallback?.(orgId, filtersToUpdate, '');
  }, [filters, chargerListCallback, createRuleFormData]);

  const onSearchTextChange = useCallback((val) => {
    chargerListCallback?.(orgId, filters, val);
  }, [filters, createRuleFormData]);

  const searchBoxConfiguration = useMemo(() => ({
    searchFieldId: 'ac_rule_chargers-search-box',
    searchFieldName: 'ac_rule_chargers-search-box',
    isDisabled: mode === 'View',
    handleSearch: onSearchTextChange,
    searchBoxClassName: 'create__configure__notification__rule__charger_selection',
  }), [onSearchTextChange, filters, createRuleFormData, mode]);

  const radioButtonList = [
    { radioButtonLabel: 'All Chargers', radioButtonId: 'AllChargers', isDisabled: mode === 'View' },
    { radioButtonLabel: 'Select Chargers', radioButtonId: 'SelectChargers', isDisabled: mode === 'View' },
  ];

  const chargerFilterProps: FilterProp[] = useMemo(() => [
    {
      filterLabel: 'Customer',
      filterType: 'dropdown',
      filterId: 'Customer',
      filterDropdownProps: {
        selectValue: filters?.find((filter) => filter?.key === 'customer')?.value,
        selectOptions: acCustomerReferenceData?.filter((c) => c?.id !== 'all') ?? [],
        handleSelectOptionChange: (val): void => handleFilterDataChange(val, 'customer'),
        selectDropDownId: 'ac-rule-customer-filter-dropdown',
        isMultiSelect: true,
        isDisabled: mode === 'View',
        renderToBody: true,
      },
    },
    {
      filterLabel: 'Location',
      filterType: 'dropdown',
      filterId: 'Location',
      filterDropdownProps: {
        selectValue: filters?.find((filter) => filter?.key === 'location')?.value,
        selectOptions: acChargerLocationReferenceData ?? [],
        handleSelectOptionChange: (val): void => handleFilterDataChange(val, 'location'),
        selectDropDownId: 'ac-rule-location-filter-dropdown',
        isMultiSelect: true,
        isDisabled: mode === 'View',
        renderToBody: true,
      },
    },
    {
      filterLabel: 'Charging Station',
      filterType: 'dropdown',
      filterId: 'chargingStationId',
      filterDropdownProps: {
        selectValue: filters?.find((filter) => filter?.key === 'chargingStationId')?.value,
        selectOptions: chargingStationReferenceData ?? [],
        handleSelectOptionChange: (val): void => handleFilterDataChange(val, 'chargingStationId'),
        selectDropDownId: 'ac-rule-station-filter-dropdown',
        isMultiSelect: true,
        isDisabled: mode === 'View',
        renderToBody: true,
      },
    },
    {
      filterLabel: 'Product Type',
      filterType: 'dropdown',
      filterId: 'ProductType',
      filterDropdownProps: {
        selectValue: filters?.find((filter) => filter?.key === 'productType')?.value,
        selectOptions: chargerProductTypeReferenceData ?? [],
        handleSelectOptionChange: (val): void => handleFilterDataChange(val, 'productType'),
        selectDropDownId: 'ac-rule-product-type-filter-dropdown',
        isMultiSelect: true,
        isDisabled: mode === 'View',
        renderToBody: true,
      },
    },
    {
      filterLabel: 'Software Version',
      filterType: 'dropdown',
      filterId: 'softwareVersion',
      filterDropdownProps: {
        selectValue: filters?.find((filter) => filter?.key === 'softwareVersion')?.value,
        selectOptions: softwareVersionReferenceData ?? [],
        handleSelectOptionChange: (val): void => handleFilterDataChange(val, 'softwareVersion'),
        selectDropDownId: 'ac-rule-sw-version-filter-dropdown',
        isMultiSelect: true,
        isDisabled: false,
        renderToBody: true,
      },
    },
  ], [acChargerLocationReferenceData, acCustomerReferenceData, filters, mode, createRuleFormData, softwareVersionReferenceData]);

  const handleSelectedValues = (selectedChargers, selectedChargerChips, isChecked) => {
    setChargerCheckboxStates(selectedChargers);
    setSelectedChargers(selectedChargers);
    setChargersChipValues(selectedChargerChips);
    setIsSelectAllChargersChecked(isChecked);
  };

  const handleRadioOptionChange = (_, value) => {
    setSelectedRadioOption(value);
    if (value === 'AllChargers') setSelectedChargers(['ALL']);
    else setSelectedChargers(chargerCheckboxStates);
  };

  const radioButtonConfiguration = useMemo(() => ({
    radionBtnGrpName: 'ac_rule_charger_options',
    buttonsList: radioButtonList,
    selectedRadioBtnValue: selectedRadioOption,
    handleSelectedRadioBtnChange: handleRadioOptionChange,
  }), [createRuleFormData, mode, selectedRadioOption, chargerCheckboxStates]);

  return (
    <>
      <DashboardLoader showLoader={showLoader} />
      <div className='user__form__wrap configure__notification'>
        <CustomBreadCrumbs breadCrumbs={breadCrumbs} />
        <div className='user__form__wrap-header-custom'>
          <DashboardHeader header={setHeaderText()} headerClassName='user__form__wrap-header-text' />
        </div>
        <div className='user__form__wrap-content'>
          <CustomForm {...createRuleFormProps} />
          <AcCreateRuleAlertLogic
            createRuleFormData={createRuleFormData}
            handleFormDataChange={handleFormDataChange}
            selectDropdownProps={{
              isMultiSelect: true,
              selectValue: alarmList?.filter((item) => createRuleFormData?.alarmTypeId?.includes(item?.id)),
              selectOptions: alarmList,
              handleSelectOptionChange: (val): void => { handleFormDataChange(val, 'alarmTypeId'); },
              selectDropDownId: 'ac-alarm-type-dropdown',
              selectDropDownName: 'ac-alarm-type-dropdown',
              isDisabled: false,
              renderToBody: true,
            }}
            inputProps={{
              inputPlaceHolder: 'Type here',
              handleInputChange: (event): void => { handleFormDataChange(event?.target?.value, 'alarmContinuesFor'); },
              inputValue: createRuleFormData?.alarmContinuesFor,
              inputType: 'string',
              inputFieldId: 'ac_alarm_continues_for',
              isRequired: false,
              isDisabled: false,
            }}
            formLabel='Alarm'
            isRequired
          />
          <CustomRadioForm
            formContainerClass={'user__form__charger-selection'}
            header={'Charger Selection*'}
            dataList={dataList || []}
            individualCheckboxStates={chargerCheckboxStates}
            selectedChipValues={chargersChipValues}
            selectAllChecked={isSelectAllChargersChecked}
            handleSelectedData={handleSelectedValues}
            filterProps={chargerFilterProps}
            radioButtonConfiguration={radioButtonConfiguration}
            smallerFont={true}
            searchBoxConfiguration={searchBoxConfiguration}
            disableCheckBox={mode === 'View'}
            showRadioList={selectedRadioOption === 'SelectChargers'}
            isCustom={true}
            radioButtonConfigNotRequired={true}
            removePadding={true}
          />

          <div className='user__form__wrap-footer-top'>
            <div
              className='user__form__wrap-footer-top-checkbox'
              onClick={() => {
                mode !== 'View' && handleFormDataChange(
                  createRuleFormData?.autoAssign === 'Enabled' ? 'Disabled' : 'Enabled',
                  'autoAssign',
                );
              }}
            >
              <CheckBox checked={createRuleFormData?.autoAssign === 'Enabled'} isDisabled={mode === 'View'} />
              Auto assign new charger to this user
            </div>
            <IconWithTooltip
              iconClassName='rms_info_icon-black'
              toolTipText={'RMS will automatically assign any new charger to this user.'}
              containerClassName='user__form_footer-auto-assign-info'
            />
          </div>

          {(showSms === true) || (showEmail === true) ? (
            <div>
              <div className='custom__form__wrap-header-text-title row__label'>
                Notification Action*
                <IconWithTooltip
                  iconClassName={'define-execution-edit-pencil-sidebar edit__button'}
                  onIconClick={() => setIsNotificationActionSidePopoverOpen(true)}
                />
              </div>
              {showSms && <div className='notification__action__row__label'>SMS : {smsRecipients && smsRecipients.length} Recipients</div>}
              {showEmail && <div className='notification__action__row__label'>Email : {emailRecipients && emailRecipients.length} Recipients</div>}
            </div>
          ) : (
            <Button
              startIcon={<AddIcon />}
              sx={{ color: '#49c8d1', textTransform: 'none', fontSize: '1rem', marginTop: '30px' }}
              onClick={() => setIsNotificationActionSidePopoverOpen(true)}
            >
              Notification Action*
            </Button>
          )}
        </div>

        <div className='user__form__wrap-footer'>
          <CustomButtonGroup
            buttonsList={mode === 'View' ? footerButtonList?.filter((button) => button?.buttonId === 'cancel') : footerButtonList}
            buttonGroupClassName='button__group__footer'
          />
        </div>
      </div>

      {isNotificationActionSidePopoverOpen && (
        <SidePopover
          id='ac_configure__notification__form__notification__action__status__popover'
          open={isNotificationActionSidePopoverOpen}
          headerText={'Notification Action'}
          containerClassName='configure__notification__detail__popover'
          handleClose={() => { }}
          headerSecondaryText={'Email/SMS Message'}
          horizontalLinePopover={true}
          popperClassName='rule__engine__raise__a__ticket'
          isRuleEnginePopOver={true}
          headerClassName={'rule__engine__header__config'}
          ruleEngineSidePopoverOnclose={handleClose}
          // eslint-disable-next-line react/no-children-prop
          children={
            <NotificationActionStatusSidePopover
              handleClose={handleClose}
              showSms={showSms}
              handleSetShowSms={handleSetShowSms}
              showEmail={showEmail}
              handleSetShowEmail={handleSetShowEmail}
              smsRecipients={smsRecipients}
              handleSmsRecipientsChange={handleSmsRecipientsChange}
              emailRecipients={emailRecipients}
              handleEmailRecipientsChange={handleEmailRecipientsChange}
            />
          }
        />
      )}
    </>
  );
};

export default AcCreateRule;
